import { LaunchTask } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { TraslatedDocumentStatus } from '../../interfaces';
import {
  convertToPT,
  mapBuildRequestStatusToReadableString,
  mapWorkStatusToReadableString,
} from './utils';

/**
 * Resolver that maps a (conference, venue) pair to the venue's canonical display
 * name — the same function the Launch-list Venue column uses to render.
 */
export type VenueDisplayNameFn = (
  conference?: string,
  venue?: string
) => string;

/**
 * Returns the free-text-search string for a given Launch-list column id and row,
 * mirroring the text that column's cell renders in ConferenceTable so a query
 * matches exactly what the user sees. Columns not handled here return '' (not
 * searched). Keep this in sync with the conferenceColumns cell renderers.
 *
 * Note two column ids whose backing field differs from the id:
 *   - 'lastBuildDate'   -> s.packageBuildInfoModifiedAt
 *   - 'lastBuildStatus' -> s.buildRequestStatus
 *
 * `venueDisplayName` is optional so callers/tests without a resolver still get
 * the raw venue id matched.
 */
export const searchableTextForColumn = (
  colId: string,
  s: LaunchTask,
  venueDisplayName?: VenueDisplayNameFn
): string => {
  switch (colId) {
    case 'name':
      return s.name ?? '';
    case 'launchId':
      return s.launchId ?? '';
    case 'conference':
      return s.conference ?? '';
    case 'venue':
      // Match the canonical display name (what the column shows) plus the raw
      // venue id (tcx-core-platforms-903 D2).
      return [
        venueDisplayName ? venueDisplayName(s.conference, s.venue) : '',
        s.venue ?? '',
      ]
        .filter(Boolean)
        .join(' ');
    case 'workStatus':
      return s.workStatus ? mapWorkStatusToReadableString(s.workStatus) : '';
    case 'writerAssignment':
      return s.writerAssignment?.alias ?? '';
    case 'productName':
      return s.productName ?? '';
    case 'launchDate':
      return convertToPT(s.launchDate);
    case 'packageBuildInfoList':
      return (s.packageBuildInfoList ?? [])
        .map((p) => p.packageName)
        .join(', ');
    case 'lastTimeToBuild':
      return convertToPT(s.lastTimeToBuild, true);
    case 'resolveVenueTime':
      return convertToPT(s.resolveVenueTime, true);
    case 'modifiedAt':
      return convertToPT(s.modifiedAt);
    case 'lastBuildDate':
      // The 'lastBuildDate' column renders the packageBuildInfoModifiedAt field.
      return convertToPT(s.packageBuildInfoModifiedAt);
    case 'lastBuildStatus':
      // The 'lastBuildStatus' column renders the buildRequestStatus field.
      return s.buildRequestStatus
        ? mapBuildRequestStatusToReadableString(s.buildRequestStatus)
        : '';
    case 'dueDate':
      return convertToPT(s.dueDate, false);
    case 'status':
      return s.status ?? '';
    case 'documentationStatus':
      return s.documentationStatus
        ? TraslatedDocumentStatus[s.documentationStatus]
        : '';
    case 'type':
      return s.type ?? '';
    default:
      return '';
  }
};
